import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Film, Globe, ExternalLink, Sparkles, ArrowRight, CheckCircle2, Calendar } from "lucide-react";
import { api } from "@/lib/api";

const countryFlags = {
  "España": "🇪🇸",
  "Estados Unidos": "🇺🇸",
  "Reino Unido": "🇬🇧",
  "Brasil": "🇧🇷",
  "Colombia": "🇨🇴",
  "Italia": "🇮🇹",
  "Japón": "🇯🇵",
  "México": "🇲🇽",
  "Argentina": "🇦🇷",
  "Chile": "🇨🇱",
  "Alemania": "🇩🇪",
  "Francia": "🇫🇷",
  "Canadá": "🇨🇦"
};

const monthMap = {
  "enero": 1, "febrero": 2, "marzo": 3, "abril": 4, "mayo": 5, "junio": 6,
  "julio": 7, "agosto": 8, "septiembre": 9, "octubre": 10, "noviembre": 11, "diciembre": 12
};

export const FestivalsSection = () => {
  const [festivals, setFestivals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFestivals();
  }, []);

  const fetchFestivals = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/festivals");
      const currentMonth = new Date().getMonth() + 1; // 1 to 12

      // Sort starting from current month ascending
      const sorted = [...data].sort((a, b) => {
        const mA = monthMap[(a.mes || a.month || "").toLowerCase()] || (a.mes_num || 1);
        const mB = monthMap[(b.mes || b.month || "").toLowerCase()] || (b.mes_num || 1);
        const diffA = (mA - currentMonth + 12) % 12;
        const diffB = (mB - currentMonth + 12) % 12;
        return diffA - diffB;
      });

      setFestivals(sorted);
    } catch (err) {
      console.error("Error fetching festivals:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-12 px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto border-t border-white/10 my-8">
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-[#E11D48]/10 border border-[#E11D48]/30 text-[#E11D48] text-xs font-semibold uppercase tracking-wider mb-2">
            <Film size={14} /> Guía Mundial de Eventos
          </div>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white flex items-center gap-2.5">
            Festivales de Cine <span className="prism-text">LGBTQ+</span>
            <Sparkles size={20} className="text-amber-400 animate-pulse" />
          </h2>
          <p className="text-zinc-400 text-xs sm:text-sm mt-1 max-w-xl">
            Próximos certámenes, muestras y convocatorias internacionales ordenados por calendario.
          </p>
        </div>

        <Link
          to="/festivales"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-zinc-900 hover:bg-zinc-800 border border-white/10 hover:border-[#E11D48]/50 text-white text-xs font-bold rounded-lg transition-all shadow-md group w-fit"
        >
          Explorar Buscador Completo
          <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform text-[#E11D48]" />
        </Link>
      </div>

      {/* Horizontal Carousel Row */}
      {loading ? (
        <div className="flex gap-4 overflow-x-hidden pb-4">
          {[1, 2, 3, 4].map(n => (
            <div key={n} className="w-72 h-80 bg-zinc-900/60 rounded-xl flex-shrink-0 animate-pulse border border-white/5" />
          ))}
        </div>
      ) : (
        <div className="flex gap-5 overflow-x-auto pb-6 scrollbar-thin scrollbar-thumb-zinc-800 scrollbar-track-transparent snap-x">
          {festivals.slice(0, 8).map(fest => {
            const country = fest.pais || fest.country || "";
            const flag = countryFlags[country] || "🌐";
            const isCallOpen = fest.estado === "Convocatoria Abierta" || Boolean(fest.call_open);
            const officialUrl = fest.enlace_oficial || fest.website_url;
            const name = fest.nombre || fest.name;
            const monthStr = fest.mes || fest.month;
            const banner = fest.banner_url || fest.image_url;

            return (
              <div
                key={fest.id}
                className="snap-start w-72 sm:w-80 flex-shrink-0 bg-zinc-900/90 hover:bg-zinc-900 border border-white/10 hover:border-[#E11D48]/50 rounded-xl overflow-hidden transition-all duration-300 flex flex-col justify-between group shadow-xl"
              >
                <div>
                  {/* Banner image with flag & month badge */}
                  <div className="relative h-40 overflow-hidden bg-zinc-950">
                    <img
                      src={banner}
                      alt={name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-80 group-hover:opacity-100"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-transparent" />

                    {/* Flag & Location */}
                    <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-black/80 backdrop-blur-md px-2.5 py-1 rounded-lg border border-white/10 text-xs font-semibold text-white">
                      <span className="text-sm">{flag}</span>
                      <span>{fest.ciudad || fest.city}, {country}</span>
                    </div>

                    {/* Status badge */}
                    <div className="absolute top-3 right-3">
                      {isCallOpen ? (
                        <span className="bg-emerald-500/90 text-white text-[10px] font-bold px-2 py-0.5 rounded-lg flex items-center gap-1 shadow-md">
                          <CheckCircle2 size={11} /> Abierta
                        </span>
                      ) : (
                        <span className="bg-zinc-800/90 text-zinc-300 text-[10px] font-medium px-2 py-0.5 rounded-lg">
                          Próxima
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Body Content */}
                  <div className="p-4">
                    <div className="flex items-center gap-2 text-xs text-amber-400 font-bold mb-1">
                      <Calendar size={13} />
                      <span>{monthStr} • {fest.region}</span>
                    </div>

                    <h3 className="text-base font-extrabold text-white group-hover:text-[#E11D48] transition-colors line-clamp-2 leading-snug mb-2">
                      {name}
                    </h3>

                    <p className="text-xs text-zinc-400 line-clamp-2 leading-relaxed mb-3">
                      {fest.description || fest.descripcion}
                    </p>
                  </div>
                </div>

                {/* Footer Link */}
                <div className="px-4 pb-4 pt-2 border-t border-white/5 flex items-center justify-between bg-zinc-950/40">
                  <span className="text-[11px] text-zinc-500 font-mono">
                    {fest.short_name || fest.id}
                  </span>
                  {officialUrl && (
                    <a
                      href={officialUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-[#E11D48] hover:text-rose-400 font-bold transition-colors"
                    >
                      Sitio Oficial <ExternalLink size={12} />
                    </a>
                  )}
                </div>
              </div>
            );
          })}

          {/* CTA Banner Card at end of row */}
          <div className="snap-start w-72 sm:w-80 flex-shrink-0 bg-gradient-to-br from-[#E11D48]/20 via-zinc-900 to-zinc-950 border border-[#E11D48]/40 rounded-xl p-6 flex flex-col justify-between shadow-2xl relative overflow-hidden group">
            <div className="absolute -right-6 -bottom-6 w-32 h-32 bg-[#E11D48]/20 rounded-full blur-2xl group-hover:scale-125 transition-transform" />

            <div>
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-lg bg-[#E11D48] text-white text-[10px] font-bold uppercase tracking-wider mb-3">
                <Film size={12} /> Catálogo Completo
              </div>

              <h3 className="text-xl font-black text-white mb-2 leading-tight">
                ¿Buscas inscripciones o más festivales?
              </h3>

              <p className="text-xs text-zinc-300 leading-relaxed mb-4">
                Accede a nuestro buscador internacional interactivo con filtros por categoría, mes y país. Exporta la guía en formato Excel / Google Sheets.
              </p>
            </div>

            <Link
              to="/festivales"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#E11D48] hover:bg-[#BE123C] text-white text-xs font-bold py-3 rounded-lg transition-all shadow-lg text-center"
            >
              Ver Todos los Festivales <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      )}
    </section>
  );
};
